/**
 * useRedemptionHistory Hook - Aggregator Pattern
 *
 * Fetches and returns redemption history data for merchants.
 */

import { useListRedemptions } from '@/generated/hooks';
import { TimestampService } from '@zlinkx/shared-v2';
import type { ListRedemptionsQueryType, ListRedemptionsResponseType } from '@zlinkx/shared-v2';
import type { AggregatorPattern } from '../../types/hook-patterns.types';
import type {
	RedemptionHistoryComputed,
	RedemptionHistoryData,
	RedemptionHistoryEntry,
	UseRedemptionHistoryOptionsInternal,
} from '@/types/controller.types';

type RedemptionHistoryApiItem = ListRedemptionsResponseType['data'][number];

function getRedemptionUserName(item: RedemptionHistoryApiItem): string {
	if (item.user_display_name !== null && item.user_display_name.trim().length > 0) {
		return item.user_display_name;
	}

	return item.user_id === null ? 'Deleted customer' : 'Unknown';
}

/**
 * Extract redemption entries from API response
 */
function extractRedemptions(data?: RedemptionHistoryApiItem[]): RedemptionHistoryEntry[] {
	if (!data || !Array.isArray(data)) return [];

	return data.map(item => ({
		redemptionId: item.redemption_id,
		couponId: item.coupon_id,
		couponCode: '',
		couponName: item.coupon_title,
		userId: item.user_id,
		userName: getRedemptionUserName(item),
		redeemedAt: item.timestamp,
		status: item.is_refunded ? 'refunded' : 'completed',
		billAmount: item.original_amount,
		originalAmount: item.original_amount,
		discountAmount: item.discount_amount,
		finalAmount: item.final_amount,
		discount: item.discount_amount,
		discountType: item.coupon_type,
		pointsAwarded: 0,
		invoiceId: item.invoice_id ?? undefined,
		redemptionNotes: item.note ?? undefined,
		notes: item.note ?? undefined,
	}));
}

function parseTimestamp(value: string | undefined): Date | null {
	if (value === undefined || value.length === 0) {
		return null;
	}

	const parsed = TimestampService.parseDateTimeInput(value);
	return TimestampService.isValidDate(parsed) ? parsed : null;
}

function formatFilterDate(value: string | undefined): string | null {
	const parsed = parseTimestamp(value);
	if (!parsed) {
		return null;
	}

	const formatted = TimestampService.formatForDisplay(parsed, { style: 'short' });
	return formatted.split(',')[0] ?? formatted;
}

function hasFilterValue(value: string | undefined): value is string {
	return value !== undefined && value.length > 0;
}

function getPeriodLabel(filters: UseRedemptionHistoryOptionsInternal['filters']): string {
	const startDate = filters?.startDate;
	const endDate = filters?.endDate;
	const hasStartDate = hasFilterValue(startDate);
	const hasEndDate = hasFilterValue(endDate);

	if (!hasStartDate && !hasEndDate) {
		return 'All Time';
	}

	const start = hasStartDate ? formatFilterDate(startDate) : null;
	const end = hasEndDate ? formatFilterDate(endDate) : null;

	if (start !== null && end !== null) {
		return `${start} - ${end}`;
	}
	if (start !== null) {
		return `Since ${start}`;
	}
	if (end !== null) {
		return `Until ${end}`;
	}

	return 'All Time';
}

function getActiveFilterCount(filters: UseRedemptionHistoryOptionsInternal['filters'], limit?: number): number {
	let count = 0;
	if (hasFilterValue(filters?.startDate)) count += 1;
	if (hasFilterValue(filters?.endDate)) count += 1;
	if (hasFilterValue(filters?.userId)) count += 1;
	if (hasFilterValue(filters?.cursor)) count += 1;
	if (limit !== undefined || filters?.limit !== undefined) count += 1;
	return count;
}

/**
 * Aggregates redemption history data
 */
export function useRedemptionHistory(options: UseRedemptionHistoryOptionsInternal): AggregatorPattern<
	RedemptionHistoryData,
	RedemptionHistoryComputed
> & {
	hasNextPage: boolean;
	fetchNextPage: () => void;
	isFetchingNextPage: boolean;
} {
	const { merchantId, filters, limit, enabled = true } = options;

	const effectiveLimit = limit ?? filters?.limit ?? 50;

	// Build query parameters (generated hook expects canonical query names)
	const queryParams: ListRedemptionsQueryType = {
		limit: effectiveLimit,
	};
	if (hasFilterValue(filters?.startDate)) queryParams.startDate = filters.startDate;
	if (hasFilterValue(filters?.endDate)) queryParams.endDate = filters.endDate;
	if (hasFilterValue(filters?.userId)) queryParams.userId = filters.userId;
	if (hasFilterValue(filters?.cursor)) queryParams.cursor = filters.cursor;

	// Fetch redemption history
	const redemptionQuery = useListRedemptions(
		{
			merchantId,
			query: queryParams,
		},
		{
			enabled: merchantId > 0 && enabled,
		},
	);

	// Extract redemptions
	const redemptions = extractRedemptions(redemptionQuery.data);
	const totalRevenue = redemptions.reduce(
		(sum, redemption) => sum + (redemption.finalAmount ?? redemption.originalAmount ?? 0),
		0,
	);
	const totalDiscounts = redemptions.reduce((sum, redemption) => sum + (redemption.discountAmount ?? 0), 0);

	const redemptionsByDay: Record<string, number> = {};
	const redemptionsByHour: Record<number, number> = {};
	for (const redemption of redemptions) {
		const redeemedDate = parseTimestamp(redemption.redeemedAt);
		if (!redeemedDate) continue;

		const dayKey = redeemedDate.toISOString().slice(0, 10);
		const hour = TimestampService.getHourFromDate(redeemedDate);
		redemptionsByDay[dayKey] = (redemptionsByDay[dayKey] ?? 0) + 1;
		redemptionsByHour[hour] = (redemptionsByHour[hour] ?? 0) + 1;
	}

	let peakDay: string | null = null;
	let peakDayCount = 0;
	for (const [day, count] of Object.entries(redemptionsByDay)) {
		if (count > peakDayCount) {
			peakDay = day;
			peakDayCount = count;
		}
	}

	let peakHour: number | null = null;
	let peakHourCount = 0;
	for (const [hourText, count] of Object.entries(redemptionsByHour)) {
		if (count > peakHourCount) {
			peakHour = Number(hourText);
			peakHourCount = count;
		}
	}

	// Basic data
	const data: RedemptionHistoryData = {
		redemptions,
		totalCount: redemptions.length,
		dateRange: {
			start: redemptions.length > 0 ? redemptions[redemptions.length - 1].redeemedAt : null,
			end: redemptions.length > 0 ? redemptions[0].redeemedAt : null,
		},
	};

	// Simple computed values
	const computed: RedemptionHistoryComputed = {
		statistics: {
			totalRedemptions: redemptions.length,
			totalRevenue,
			totalDiscounts,
			averageOrderValue: redemptions.length > 0 ? totalRevenue / redemptions.length : 0,
			averageDiscount: redemptions.length > 0 ? totalDiscounts / redemptions.length : 0,
			redemptionsByDay,
			redemptionsByHour,
			topCoupons: [],
			topCustomers: [],
			peakDay,
			peakHour,
		},
		filteredRedemptions: redemptions,
		hasData: redemptions.length > 0,
		isEmpty: redemptions.length === 0,
		isFiltered: getActiveFilterCount(filters, limit) > 0,
		activeFilterCount: getActiveFilterCount(filters, limit),
		periodLabel: getPeriodLabel(filters),
	};

	// Return aggregator pattern
	return {
		data,
		computed,
		isLoading: redemptionQuery.isLoading,
		error: redemptionQuery.error?.message ?? null,

		// Pagination (from paginated query hook)
		hasNextPage: redemptionQuery.hasNextPage,
		fetchNextPage: redemptionQuery.fetchNextPage,
		isFetchingNextPage: redemptionQuery.isFetchingNextPage,
	};
}

export default useRedemptionHistory;
